import React, { useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  Heading,
  Text,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export default function ClientCheck() {
  const [clientCode, setClientCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const originalInput = clientCode.trim()

      // Detect if input looks like an email
      const payload = originalInput.includes('@')
        ? { email: originalInput.toLowerCase() }
        : { user_login: originalInput }

      const resp = await apiClient.post('/check-user', payload)
      const data = resp.data

      if (data.exists) {
        if (data.requires_password_setup) {
          navigate(`/create-password?client=${encodeURIComponent(originalInput)}&userId=${data.user_id}`)
        } else {
          // User already set up, redirect to login with prefilled client/user
          window.location.href = `/login?client=${encodeURIComponent(originalInput)}&already_activated=1`
        }
      } else {
        setError('No user found for that identifier. Please check and try again.')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to check user. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box minH="100vh" bg={useThemeColors().bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={useThemeColors().cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img src={useSiteSettings().settings.auth_logo || '/assets/img/als-logo-2.png'} alt="ActionLabs" style={{ maxWidth: '300px' }} />
              </Box>

              <Heading size="lg" color={useThemeColors().heading} mb={1}>
                Verify Client Account
              </Heading>
              <Text color={useThemeColors().text} fontSize="sm">
                Enter your Client ID to check account setup
              </Text>
            </Box>

            <Box as="form" onSubmit={onSubmit} w="100%">
              <VStack spacing={3}>
                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Client ID</FormLabel>
                  <Input
                    value={clientCode}
                    onChange={(e) => setClientCode(e.target.value)}
                    type="text"
                    placeholder="Enter your Client ID, username, or email (e.g. client123 or user@example.com)"
                  />
                </FormControl>

                <Button type="submit" colorScheme="blue" isLoading={loading} w="full">
                  Continue
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
